import { ConfirmModal, PermissionsList } from "@/components";
import { RIGHTS } from "@/enums";
import { routes } from "@/helpers";
import { compareStrings } from "@/helpers/tableSorter";
import { useToggle } from "@/hooks";
import { useUserContext } from "@/providers";
import {
  useDeletePermissionMutation,
  useGetPermissionsAssignmentCountQuery,
} from "@/services";
import {
  faCirclePlus,
  faEdit,
  faTrash,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "antd";
import { useState } from "react";
import { useIntl } from "react-intl";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { BooleanParam, useQueryParam } from "use-query-params";

const Permissions = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { hasRight } = useUserContext();

  const canEditPermissions = hasRight([RIGHTS.writePermissions]);
  const [permissions, setPermissions] = useState();

  const columns = [
    {
      title: intl.formatMessage({ id: "permissions.colName" }),
      dataIndex: "name",
      sorter: {
        compare: (a, b) => compareStrings(a.name, b.name),
      },
    },
    {
      title: intl.formatMessage({ id: "permissions.colDescription" }),
      dataIndex: "description",
      sorter: {
        compare: (a, b) => compareStrings(a.description, b.description),
      },
    },
    {
      title: intl.formatMessage({ id: "general.actions" }),
      render: (permission) => (
        <>
          <Link to={routes.admin.permissions.get(permission.id)}>
            <Button
              type="link"
              data-test-id={`permissions-edit-${permission.id}`}
              icon={<FontAwesomeIcon icon={faEdit} />}
            />
          </Link>
          {canEditPermissions && (
            <Button
              type="link"
              data-test-id={`permissions-delete-${permission.id}`}
              onClick={() => handleModalToggle(permission)}
              icon={<FontAwesomeIcon icon={faTrash} />}
            />
          )}
        </>
      ),
    },
  ];

  const [includeDeleted, setIncludeDeleted] = useQueryParam(
    "includeDeleted",
    BooleanParam
  );

  const [confirmationModalHidden, toggleConfirmationModal] = useToggle(true);
  const [permissionToDelete, setPermissionToDelete] = useState();

  const {
    data: permissionAssignmentCount,
    isFetching: getPermissionCountsLoading,
  } = useGetPermissionsAssignmentCountQuery(permissionToDelete?.id, {
    skip: !permissionToDelete?.id,
  });
  const [deletePermission] = useDeletePermissionMutation();

  const handleModalToggle = (permission) => {
    setPermissionToDelete(permission.id);
    toggleConfirmationModal();
  };

  const advancedSearchFields = {
    name: {
      searchName: "name",
      label: intl.formatMessage({ id: "permissions.colName" }),
    },
    description: {
      searchName: "description",
      label: intl.formatMessage({ id: "permissions.colDescription" }),
    },
    includeDeleted: {
      labelName: "includeDeleted",
      type: "checkbox",
      label: intl.formatMessage({ id: "permissions.includeDeleted" }),
      setValue: (value) => {
        setIncludeDeleted(value, "pushIn");
      },
      value: includeDeleted,
      row: "2",
    },
  };

  const handleDelete = () => {
    deletePermission(permissionToDelete)
      .unwrap()
      .then(() => {
        setPermissions(
          permissions?.filter(({ id }) => id !== permissionToDelete)
        );
        setPermissionToDelete();
        toggleConfirmationModal();
        toast.success(intl.formatMessage({ id: "general.toastChangesSaved" }));
      })
      .catch(() => {
        toast.error(
          intl.formatMessage({ id: "general.toastSomethingWentWrong" })
        );
      });
  };

  return (
    <>
      <PermissionsList
        pageTitle={intl.formatMessage({ id: "permissions.title" })}
        columns={columns}
        stateKey="permissions"
        additionalQueryStringParams={{
          includeDeleted,
        }}
        searchWithNoQuery={true}
        customActions={[
          {
            label: intl.formatMessage({ id: "general.add" }),
            icon: <FontAwesomeIcon icon={faCirclePlus} />,
            onClick: () => navigate(routes.admin.permissions.add),
          },
        ]}
        permissions={permissions}
        setPermissions={setPermissions}
        advancedSearchFields={advancedSearchFields}
        showAdvanced={true}
        showFilter={false}
        searchHelpText={intl.formatMessage({
          id: "permissions.searchHelpText",
        })}
        searchPlaceholder={intl.formatMessage({
          id: "permissions.searchPlaceholder",
        })}
      />
      <ConfirmModal
        onSaveChanges={handleDelete}
        hidden={confirmationModalHidden}
        toggle={toggleConfirmationModal}
        headerTitleKey={"permissions.deletePermissionModalTitle"}
        bodyTextKey={"permissions.deletePermissionModalDescription"}
        additionalBodyText={
          permissionAssignmentCount
            ? intl.formatMessage(
                {
                  id: "permissions.userCountMessage",
                },
                {
                  userCount: permissionAssignmentCount,
                }
              )
            : ""
        }
        loading={getPermissionCountsLoading}
        confirmationTimeoutSeconds={5}
      />
    </>
  );
};

export default Permissions;
